import { defineStore } from 'pinia'
import {
  toUtf8,
  toBase64,
  Delegation,
  MitoWhitelistAccount,
  WasmContractQueryResponse,
  MitoIDOSubscriptionActivity
} from '@injectivelabs/sdk-ts'
import { wasmApi, mitoApi } from '@shared/Service'
import { ZERO_IN_BASE } from '@shared/utils/constant'
import {
  finalizeIDO,
  instantiateIDO,
  launchSpotMarket,
  grantOneTimeAllowance,
  addWhitelistedAccounts,
  updateProjectDescription,
  removeWhitelistedAccounts,
  queryUserHasOneTimeAllowance
} from '@/store/launchpad/admin'
import {
  subscribeToIDO,
  claimIDORewards,
  claimVestedRewards,
  instantiateVestingClaim
} from '@/store/launchpad/message'
import { LAUNCHPAD_FACTORY_CONTRACT_ADDRESS } from '@/app/utils/constant'
import { MitoConfigTransformer } from '@/app/client/transformers/config'
import { UiLaunchpadTransformer } from '@/app/client/transformers/launchpad'
import {
  UIIDO,
  UIIDOSubscriber,
  UIClaimReference,
  UIIDOSubscription,
  LaunchpadFactoryConfig
} from '@/types'

type LaunchpadStoreState = {
  idos: UIIDO[]
  totalIdos: number
  totalActivities: number
  totalSubscribers: number
  delegations: Delegation[]
  config: LaunchpadFactoryConfig
  subscribers: UIIDOSubscriber[]
  subscription?: UIIDOSubscription
  totalWhitelistedAccounts: number
  currentClaimableAmount: string
  claimReference?: UIClaimReference
  activities: MitoIDOSubscriptionActivity[]
  whitelistedAccounts: MitoWhitelistAccount[]
}

const initialStateFactory = (): LaunchpadStoreState => ({
  idos: [],
  totalIdos: 0,
  activities: [],
  delegations: [],
  subscribers: [],
  totalActivities: 0,
  totalSubscribers: 0,
  subscription: undefined,
  whitelistedAccounts: [],
  claimReference: undefined,
  currentClaimableAmount: '',
  totalWhitelistedAccounts: 0,
  config: {} as LaunchpadFactoryConfig
})

export const useLaunchpadStore = defineStore('launchpad', {
  state: (): LaunchpadStoreState => initialStateFactory(),
  getters: {
    instantiationFee: (state: LaunchpadStoreState) => {
      return state.config?.launchFee || '100000000000000000000'
    },
    totalStaked: (state: LaunchpadStoreState) => {
      return state.delegations
        .reduce(
          (sum, delegation: Delegation) => sum.plus(delegation.balance.amount),
          ZERO_IN_BASE
        )
        .toNumber()
    }
  },
  actions: {
    finalizeIDO,
    instantiateIDO,
    subscribeToIDO,
    claimIDORewards,
    launchSpotMarket,
    claimVestedRewards,
    grantOneTimeAllowance,
    addWhitelistedAccounts,
    instantiateVestingClaim,
    updateProjectDescription,
    removeWhitelistedAccounts,
    queryUserHasOneTimeAllowance,

    async fetchFactoryConfig() {
      const launchpadStore = useLaunchpadStore()

      const response = (await wasmApi.fetchSmartContractState(
        LAUNCHPAD_FACTORY_CONTRACT_ADDRESS,
        toBase64({ Config: {} })
      )) as WasmContractQueryResponse

      const config =
        MitoConfigTransformer.configResponseToLaunchpadFactoryConfig(response)

      launchpadStore.config = config
    },

    async fetchIDO(contractAddress: string) {
      const launchpadStore = useLaunchpadStore()
      const sharedWalletStore = useSharedWalletStore()

      const { ido } = await mitoApi.fetchIDO({
        contractAddress,
        accountAddress: sharedWalletStore.injectiveAddress
      })

      if (!ido) {
        return
      }

      const filteredIdos = launchpadStore.idos.filter(
        (ido) => ido.contractAddress !== contractAddress
      )

      launchpadStore.$patch({
        idos: [...filteredIdos, UiLaunchpadTransformer.idoToUIIdo(ido)].filter(
          (ido) => ido
        )
      })
    },

    async fetchIDOs({
      status,
      limit,
      toNumber,
      accountAddress
    }: {
      status?: string
      limit?: number
      toNumber?: number
      accountAddress?: string
    } = {}) {
      const launchpadStore = useLaunchpadStore()
      const sharedWalletStore = useSharedWalletStore()

      const { idos, pagination } = await mitoApi.fetchIDOs({
        status,
        limit,
        toNumber,
        accountAddress,
        ownerAddress: sharedWalletStore.injectiveAddress
      })

      launchpadStore.$patch({
        idos: idos.map(UiLaunchpadTransformer.idoToUIIdo).filter((ido) => ido),
        totalIdos: Number(pagination?.total || 0)
      })
    },

    async fetchIDOSubscribers({
      contractAddress,
      limit,
      skip
    }: {
      contractAddress: string
      limit?: number
      skip?: number
    }) {
      const launchpadStore = useLaunchpadStore()

      const response = await mitoApi.fetchIDOSubscribers({
        contractAddress,
        limit,
        skip
      })

      launchpadStore.$patch({
        subscribers:
          UiLaunchpadTransformer.subscribersResponseToUiSubscribers(response),
        totalSubscribers: Number(response.pagination?.total || 0)
      })
    },

    async fetchIDOSubscription(contractAddress: string) {
      const launchpadStore = useLaunchpadStore()
      const sharedWalletStore = useSharedWalletStore()

      if (
        !sharedWalletStore.isUserConnected ||
        !sharedWalletStore.injectiveAddress
      ) {
        return
      }

      const { subscription } = await mitoApi.fetchIDOSubscription({
        contractAddress,
        accountAddress: sharedWalletStore.injectiveAddress
      })

      launchpadStore.$patch({
        subscription: UiLaunchpadTransformer.subscriptionToUiSubscription({
          subscription,
          contractAddress
        })
      })
    },

    async fetchIDOActivities({
      limit,
      toNumber,
      accountAddress,
      contractAddress
    }: {
      limit?: number
      toNumber?: string
      accountAddress?: string
      contractAddress?: string
    }) {
      const launchpadStore = useLaunchpadStore()

      const { activities, pagination } = await mitoApi.fetchIDOActivities({
        limit,
        toNumber,
        accountAddress,
        contractAddress
      })

      launchpadStore.$patch({
        activities,
        totalActivities: Number(pagination?.total || 0)
      })
    },

    async fetchIDOWhitelistedAccounts(idoAddress: string) {
      const launchpadStore = useLaunchpadStore()

      const { accounts, pagination } = await mitoApi.fetchIDOWhitelist({
        idoAddress
      })

      const totalWhitelistedAccounts = Number(pagination?.total || 0)

      if (totalWhitelistedAccounts <= 100) {
        launchpadStore.$patch({
          totalWhitelistedAccounts,
          whitelistedAccounts: accounts
        })

        return
      }

      const LIMIT = 100
      const totalBatchesBasedOnLimit = Array.from(
        Array(Math.floor(totalWhitelistedAccounts / LIMIT)).keys()
      )

      const results = await Promise.all(
        totalBatchesBasedOnLimit.map((count) =>
          mitoApi.fetchIDOWhitelist({
            idoAddress,
            limit: LIMIT,
            skip: (count + 1) * LIMIT
          })
        )
      )

      const whitelistedAccounts = results.reduce(
        (list: MitoWhitelistAccount[], result) => [...list, ...result.accounts],
        accounts as MitoWhitelistAccount[]
      )

      launchpadStore.$patch({
        whitelistedAccounts,
        totalWhitelistedAccounts
      })
    },

    async fetchClaimReference(idoAddress: string) {
      const launchpadStore = useLaunchpadStore()
      const sharedWalletStore = useSharedWalletStore()

      if (
        !sharedWalletStore.isUserConnected ||
        !sharedWalletStore.injectiveAddress
      ) {
        return
      }

      const { claimReferences } = await mitoApi.fetchClaimReferences({
        idoAddress,
        accountAddress: sharedWalletStore.injectiveAddress
      })

      const [claimReference] = claimReferences

      if (!claimReference) {
        launchpadStore.$patch({
          claimReference: undefined
        })

        return
      }

      await launchpadStore.fetchVestingClaimableRewards(
        claimReference.cwContractAddress
      )

      launchpadStore.$patch({
        claimReference: UiLaunchpadTransformer.claimReferenceToClaimReference(
          claimReferences[0]
        )
      })
    },

    async fetchVestingClaimableRewards(contractAddress: string) {
      const launchpadStore = useLaunchpadStore()

      const response = (await wasmApi.fetchSmartContractState(
        contractAddress,
        toBase64({ distributable: {} })
      )) as WasmContractQueryResponse

      launchpadStore.$patch({
        currentClaimableAmount: JSON.parse(toUtf8(response.data))
      })
    },

    resetClaimReference() {
      const launchpadStore = useLaunchpadStore()

      launchpadStore.claimReference = undefined
      launchpadStore.currentClaimableAmount = ''
    },

    resetUserState() {
      const launchpadStore = useLaunchpadStore()

      launchpadStore.$patch({
        subscription: undefined,
        claimReference: undefined,
        currentClaimableAmount: ''
      })
    }
  }
})
